import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Box, Flex, Table, Thead, Tbody, Tr, Th, Td, Avatar, Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure, Button, Text, VStack, useBreakpointValue, useColorModeValue } from '@chakra-ui/react';
import PlayerCard from './PlayerCard';
import SignFreeAgentModal from './SignFreeAgentModal';
import { displayPlayerName, DisplayPlayerNameSize } from '../utils/displayPlayerName';
import { formatSalary } from '../utils/formatMoney';
import { CURRENT_YEAR } from '../constants';
import { formatPstDateTime, getPstTimeDifference } from '../lib/pstTime';
import apiClient from '../lib/api/apiClient';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserContext } from '../contexts/UserContext';
import { useModernToast } from './ModernToast';
import { getTeamNameById, getTeamInfoById } from '../utils/getTeamInfo';
import { useTeam } from '../hooks/useTeam';
import { useTeamRoster } from '../hooks/useTeamRoster';
import StripePaymentForm from './StripePreApproval';
import { useUser } from '../hooks/useUser';
import { useUserStore } from '../store/userStore';
import { formatPositionsList } from '../utils/formatPositionsList';
import { httpsPlayerImageUrl } from '../utils/stringUtils';

/**
 * Season buckets from `rwPlayer.seasonPositions` (same shape as roster lineups).
 * If primary/secondary/tertiary arrays are all empty, falls back to legacy
 * `Position_1` / `position_1`, `position_2`, `position_3` (comma-separated within each tier, `/` between tiers).
 */
function positionsFromRwSeason(entry: any): string {
  const rw = entry.rwPlayer;
  const sp = rw?.seasonPositions;
  const prim = Array.isArray(sp?.primaryPosition) ? sp.primaryPosition.filter(Boolean) : [];
  const sec = Array.isArray(sp?.secondaryPosition) ? sp.secondaryPosition.filter(Boolean) : [];
  const ter = Array.isArray(sp?.tertiaryPosition) ? sp.tertiaryPosition.filter(Boolean) : [];
  const bucketsEmpty = prim.length === 0 && sec.length === 0 && ter.length === 0;

  if (!bucketsEmpty) {
    return formatPositionsList({
      primaryPosition: sp?.primaryPosition,
      secondaryPosition: sp?.secondaryPosition,
      tertiaryPosition: sp?.tertiaryPosition
    });
  }

  const tier = (v: unknown) => String(v ?? '').trim();
  const p1 = tier(rw?.Position_1 ?? rw?.position_1);
  const p2 = tier(rw?.position_2);
  const p3 = tier(rw?.position_3);
  const legacyParts = [p1, p2, p3].filter((s) => s.length > 0);
  return legacyParts.length ? legacyParts.join('/') : '—';
}

// Constants for gradient and frost wash (shared across all instances)
const PAGE_GRADIENT = `linear-gradient(165deg, black 0%, #002e0b 80vh, #002e0b 100%)`;
const FROST_WASH = `inset 0 0 0 9999px rgba(255,255,255,0.06)`;

interface AvailablePlayersContentProps {
  tabName: 'Players' | 'Additional Info' | 'Contract Details';
  players: any[];
  leagueTeam?: any;
  isWaiverWireView?: boolean;
  isFreeAgentView?: boolean;
  refetch?: () => void;
  systemSettings?: any;
  viewAcceptingOffers?: boolean;
}

const AvailablePlayersContent: React.FC<AvailablePlayersContentProps> = ({
  tabName, players,
  isWaiverWireView = false, isFreeAgentView = false,
  refetch, systemSettings, viewAcceptingOffers }) => {
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const { myTeam } = useUserContext();
  const { user } = useUserStore();
  const toast = useModernToast();
  const playerNameSize = useBreakpointValue<DisplayPlayerNameSize>({ base: 'md', md: 'full' }) ?? 'md';
  const seasonStatus = myLeague?.id === 9 ? "Off Season" : systemSettings?.seasonStatus;
  // Get current team's payroll and salary cap for validation
  const { team: currentTeamData } = useTeam(myTeam || undefined);
  const { total_payroll: currentPayroll } = useTeamRoster((myTeam ?? 1) as number);

  // Stripe pre-approval state for salary cap exceedance
  const [isPreApprovalOpen, setIsPreApprovalOpen] = useState(false);
  const [preApprovalAmount, setPreApprovalAmount] = useState(0);
  const [pendingClaimSubmission, setPendingClaimSubmission] = useState<(() => Promise<void>) | null>(null);
  const [pendingClaimPlayer, setPendingClaimPlayer] = useState<any>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  const stickyBg = useColorModeValue('white', 'gray.800');

  const renderPlayerCell = (entry: any, showOfferButton: boolean) => {
    const myTeamOfferInfo = getMyTeamOfferInfo(entry);
    const isMyTeamPlayer = myTeam && entry.team_id && Number(entry.team_id) === Number(myTeam);
    const hasMyTeamOffer = myTeamOfferInfo.hasOffer;
    const playerName = displayPlayerName({
      firstName: entry.rwPlayer?.first_name,
      lastName: entry.rwPlayer?.last_name,
      fullName: entry.rwPlayer?.full_name || entry.player?.name,
      name: entry.name,
    }, playerNameSize);

    // Check expiration for mobile button

    const isExpired = entry.dealData && entry.dealData.expires && getPstTimeDifference(entry.dealData.expires) <= 0;
    const headshotUrl = httpsPlayerImageUrl(entry.rwPlayer?.image);

    return (
      <VStack align="flex-start" spacing={isFreeAgentView ? 1 : 0}>
        <Flex align="center">
          <div className="free-agent-avatar-wrapper">
            {headshotUrl && (
              <div className="free-agent-avatar-bg" />
            )}
            <Avatar className="free-agent-avatar" size="md" src={headshotUrl} name={playerName} ignoreFallback={false} position="relative" zIndex={1} bg={getTeamInfoById(entry.team_id, myLeague)?.color_primary || "gray.600"} color={getTeamInfoById(entry.team_id, myLeague)?.color_text || "white"} />
          </div>
          <span className="free-agent-player-name">{playerName}</span>
        </Flex>
        {showOfferButton && isFreeAgentView && !isExpired && (
          <Button
            size="xs"
            variant="dominus"
            colorScheme="blue"
            onClick={e => { e.stopPropagation(); handleUserAction(entry, 'make_offer'); }}
            isDisabled={
              entry.deal_status === 'settlement_free_agent_offer' ||
              seasonStatus === 'Off Season Quiet'
            }
            display={{ base: 'flex', md: 'none' }}
            mt={1}
            alignSelf="flex-start"
          >
            {hasMyTeamOffer ? 'UPDATE OFFER' : 'Make Offer'}
          </Button>
        )}
      </VStack>
    );
  };

  // Helper function to check if my team has made an offer and return the offer info
  const getMyTeamOfferInfo = (entry: any) => {
    if (myTeam && entry.dealData && entry.dealData.offers) {
      // Use spread and reverse to find the most recent offer from the user's team
      const myTeamOffer = [...entry.dealData.offers].reverse().find((offer: any) => Number(offer.team) === Number(myTeam));
      if (myTeamOffer) {
        const offerDate = myTeamOffer.created ? formatPstDateTime(myTeamOffer.created) : 'unknown date';
        return {
          hasOffer: true,
          date: offerDate
        };
      }
    }
    return { hasOffer: false, date: null };
  };

  // Helper function to render the "Make Offer" button for free agents
  const renderMakeOfferButton = (entry: any) => {
    // Check if deal is expired
    if (entry.dealData && entry.dealData.expires) {
      const diffMinutes = getPstTimeDifference(entry.dealData.expires);
      if (diffMinutes <= 0) {
        return null; // Don't show button if expired
      }
    }

    // Check if my team has made an offer
    const hasMyTeamOffer = Boolean(
      (myTeam && entry.dealData && entry.dealData.offers && entry.dealData.offers.some((offer: any) => Number(offer.team) === Number(myTeam))) ||
      (entry.dealData && (entry.dealData as any).myTeamHasOffer === true)
    );
    return (
      <Button
        style={{ marginRight: '12px' }}
        size="sm"
        colorScheme="blue"
        isDisabled={
          entry.deal_status === 'settlement_free_agent_offer' ||
          seasonStatus === 'Off Season Quiet'
        }
        onClick={e => { e.stopPropagation(); handleUserAction(entry, 'make_offer'); }}
      >
        {hasMyTeamOffer ? 'UPDATE OFFER' : 'Make Offer'}
      </Button>
    );
  };

  // State for SignFreeAgentModal
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [signModalPlayer, setSignModalPlayer] = useState<any>(null);
  const [signModalLevel, setSignModalLevel] = useState<'MLB' | 'AAA'>('MLB');

  // Column configs moved inside component to access myLeague
  const notesHeader = isWaiverWireView ? 'Claims' : (isFreeAgentView ? 'Offers' : 'Notes');

  const freeAgentColumnsConfig: Record<string, any[]> = {
    'Players': [
      {
        header: 'Player',
        key: 'player',
        render: (entry: any) => renderPlayerCell(entry, true),
      },
      {
        header: 'POS (B/T)',
        key: 'pos_bt',
        thProps: { style: { width: '130px', minWidth: '130px', maxWidth: '130px' } },
        tdProps: { style: { width: '130px', minWidth: '130px', maxWidth: '130px' } },
        render: (entry: any) => {
          const positionsText = positionsFromRwSeason(entry);
          const bats = entry.rwPlayer.bats || '-';
          const throws = entry.rwPlayer.throws || '-';
          const btText = `${bats}/${throws}`;

          return (
            <VStack align="flex-start" spacing={1}>
              <Text>{positionsText}</Text>
              <Text fontSize="xs" color="gray.400">{btText}</Text>
            </VStack>
          );
        },
      },
      {
        header: notesHeader,
        key: 'details',
        thProps: { style: { width: '190px', minWidth: '190px', maxWidth: '190px' } },
        tdProps: { style: { width: '190px', minWidth: '190px', maxWidth: '190px', fontSize: '12px' } },
        render: (entry: any) => {
          const notes = [];

          // Check for settlement status first
          if (entry.deal_status === 'settlement_free_agent_offer') {
            // Get team name for the signing team
            const signingTeam = entry.dealData?.teams && entry.dealData.teams.length > 0
              ? getTeamNameById(entry.dealData.teams[0], myLeague)
              : 'Unknown Team';

            notes.push(
              <div key="settlement" style={{ marginBottom: '4px' }}>
                <div style={{ color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }}>
                  Signed by {signingTeam}
                </div>
                <div style={{ color: '#48bb78' }}>
                  Settlement closes at midnight
                </div>
              </div>
            );
          } else if (entry.deal_status === 'settlement_waiver_claim') {
            // Get team name for the claiming team
            const claimingTeam = entry.dealData?.teams && entry.dealData.teams.length > 0
              ? getTeamNameById(entry.dealData.teams[0], myLeague)
              : 'Unknown Team';

            notes.push(
              <div key="settlement" style={{ marginBottom: '4px' }}>
                <div style={{ color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }}>
                  Claimed by {claimingTeam}
                </div>
                <div style={{ color: '#48bb78' }}>
                  Settlement closes at midnight
                </div>
              </div>
            );
          }
          // Add bid information if dealData has bids and not in settlement
          else if (entry.dealData && entry.dealData.bids > 0) {
            const offerText = entry.dealData.bids === 1 ? '1 offer' : `${entry.dealData.bids} offers`;
            const offerVerb = entry.dealData.bids === 1 ? 'is' : 'are';
            const checkMarks = '✓'.repeat(entry.dealData.bids);
            notes.push(
              <div key="bids" style={{ marginBottom: '4px' }}>
                {offerText} made&nbsp;&nbsp;<span style={{ color: '#48bb78' }}>{checkMarks}</span>
              </div>
            );

            // Check if my team has made an offer
            const myTeamOfferInfo = getMyTeamOfferInfo(entry);
            if (myTeamOfferInfo.hasOffer) {
              let isWinning = false;
              if (entry.dealData?.offers && entry.dealData.offers.length > 0) {
                const highestOfferScore = entry.dealData.offers.reduce((max: number, offer: any) =>
                  Math.max(max, offer.offer?.offerScore || 0), 0);
                const myOffer = entry.dealData.offers.find((offer: any) => Number(offer.team) === Number(myTeam));
                if (myOffer && myOffer.offer?.offerScore && Math.abs(myOffer.offer.offerScore - highestOfferScore) < 0.001 && highestOfferScore > 0) {
                  isWinning = true;
                }
              }

              notes.push(
                <div key="my-team-offer" className="dominus-blue" style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                  Your team submitted an offer on {myTeamOfferInfo.date}
                  {isWinning && user?.systemSettings?.seasonStatus !== "In Season" && (
                    <span style={{ color: '#ff9b21', marginLeft: '0px', display: 'block' }}>
                      Current High Bidder
                    </span>
                  )}
                </div>
              );
            }

            // Display Top Offer Score if available

            if (entry?.dealData?.bids > 0 && entry?.dealData?.offers) {
              const highestOfferScore = entry.dealData.offers.reduce((max: number, offer: any) =>
                Math.max(max, offer.offer?.offerScore || 0), 0);

              if (highestOfferScore > 0 && user?.systemSettings?.seasonStatus !== "In Season") {
                const score = (highestOfferScore / 1000000).toFixed(2);
                notes.push(
                  <div key="offer-score" style={{ marginBottom: '4px', fontSize: '13px', fontStyle: 'italic', color: '#718096' }}>
                    Top Offer Score: {score}
                  </div>
                );
              }
            }

            if (entry.dealData.expires) {
              notes.push(
                <div key="expires" style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                  {getPstTimeDifference(entry.dealData.expires) > 0 ? (
                    <>
                      <span
                        style={{
                          color: '#48bb78',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleUserAction(entry, 'sign', 'MLB');
                        }}
                      >
                        Accepting offers
                      </span> until {formatPstDateTime(entry.dealData.expires, true)}
                    </>
                  ) : (
                    <span style={{ color: '#e53e3e', fontWeight: 'bold' }}>
                      No longer accepting offers as of {formatPstDateTime(entry.dealData.expires, true)}
                    </span>
                  )}
                </div>
              );
            }
          }

          // Add claims information for waiver wire players (but not if in settlement)
          if (entry.dealData && entry.dealData.claims && entry.dealData.claims > 0 && entry.deal_status !== 'settlement_waiver_claim') {
            const claimsText = entry.dealData.claims === 1 ? '1 claim' : `${entry.dealData.claims} claims`;
            const checkMarks = '✓'.repeat(Math.min(entry.dealData.claims, 5));
            notes.push(
              <div key="claims" style={{ marginBottom: '4px' }}>
                {claimsText} submitted&nbsp;&nbsp;<span style={{ color: '#48bb78' }}>{checkMarks}</span>
              </div>
            );
          }

          // Add existing trade status information (for free agents) or deal status (for waiver wire)
          if (entry.trade_status_until) {
            notes.push(
              <div key="claims" style={{ fontStyle: 'italic', fontSize: '0.95em', marginTop: notes.length > 0 ? '4px' : '0' }}>
                Claims through<br />{formatPstDateTime(entry.trade_status_until)}
              </div>
            );
          } else if (entry.deal_until && entry.deal_status !== 'free_agent_offer' && entry.deal_status !== 'settlement_free_agent_offer' && entry.deal_status !== 'settlement_waiver_claim') {
            // Show waivers expire message (skip free agent offers since "Accepting offers until" already shows the time)
            notes.push(
              <div key="deal-until" style={{ fontStyle: 'italic', fontSize: '0.95em', marginTop: notes.length > 0 ? '4px' : '0' }}>
                Waivers expire<br />{formatPstDateTime(entry.deal_until)}
              </div>
            );
          }

          return notes.length > 0 ? (
            <div style={{ textAlign: 'left', display: 'block' }}>
              {notes}
            </div>
          ) : null;
        },
      },
      {
        header: isFreeAgentView ? 'Previous Team' : 'Current Team',
        key: 'current_team',
        thProps: { style: { minWidth: '140px' } },
        tdProps: { style: { minWidth: '140px' } },
        render: (entry: any) => {
          // For free agent view (not waiver wire), implement the three-step logic
          if (isFreeAgentView && !isWaiverWireView) {
            // 1) If there is a team_id in the player's data, use that
            if (entry.team_id || entry.old_team_id) {
              const teamId = entry.old_team_id || entry.team_id;
              return getTeamNameById(teamId, myLeague) || `ID: ${entry.team_id}`;
            }

            // 2) If team_id is null, look for PlayerContract.team_id where carried === true
            if (entry.playerContract && entry.playerContract.team_id && entry.playerContract.carried === true) {
              return getTeamNameById(entry.playerContract.team_id, myLeague) || `ID: ${entry.playerContract.team_id}`;
            }

            // 3) If neither condition, put a dash
            return '-';
          }

          // Default logic for other views
          return getTeamNameById(entry.team_id, myLeague) || `ID: ${entry.team_id}`;
        },
      },
      {
        header: 'Salary',
        key: 'salary',
        thProps: { style: { minWidth: '90px' } },
        tdProps: { style: { minWidth: '90px' } },
        render: (entry: any) => formatSalary(entry.playerContract?.current_salary, false),
      },
      {
        header: 'Total Contract',
        key: 'total_contract',
        thProps: { style: { minWidth: '120px' } },
        tdProps: { style: { minWidth: '120px' } },
        render: (entry: any) => {
          const val = entry.playerContract?.total_value;
          let totalValueNum: number | null = null;
          if (typeof val === 'string') totalValueNum = parseFloat(val);
          else if (typeof val === 'number') totalValueNum = val;
          if (totalValueNum !== null && !isNaN(totalValueNum)) return `$${totalValueNum.toLocaleString()}`;
          if (entry.playerContract?.salaries && entry.playerContract.salaries.length > 0) {
            const total = entry.playerContract.salaries.reduce((sum: number, s: { salary: number | string | undefined }) => {
              let salaryNum = 0;
              if (typeof s.salary === 'string') salaryNum = parseFloat(s.salary);
              else if (typeof s.salary === 'number') salaryNum = s;
              return sum + (isNaN(salaryNum) ? 0 : salaryNum);
            }, 0);
            return `$${total.toLocaleString()}`;
          }
          return '-';
        },
      },
      {
        header: '',
        key: 'claim',
        render: (entry: any, isWaiverWireView: boolean) => {
          if (isWaiverWireView) {
            // Check if the player's current team is my team
            const isMyTeamPlayer = myTeam && entry.team_id && Number(entry.team_id) === Number(myTeam);

            if (isMyTeamPlayer) {
              return null; // Don't show any button for players on my team
            }

            // Check if my team has already claimed this player
            const hasMyTeamClaimed = entry.dealData?.teams && myTeam &&
              entry.dealData.teams.some((teamId: any) => Number(teamId) === Number(myTeam));

            if (hasMyTeamClaimed) {
              return (
                <Text className="dominus-blue" style={{ marginRight: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                  Your team has submitted a claim
                </Text>
              );
            }

            return (
              <Button style={{ marginRight: '12px' }} size="sm" colorScheme="blue" isDisabled={entry.deal_status === 'settlement_waiver_claim'} onClick={e => { e.stopPropagation(); if (typeof entry.handleClaimWaiver === 'function') entry.handleClaimWaiver(entry); }}>
                Claim
              </Button>
            );
          } else {
            return renderMakeOfferButton(entry);
          }
        },
      },
    ],
    'Additional Info': [
      {
        header: 'Player',
        key: 'player',
        render: (entry: any) => renderPlayerCell(entry, false),
      },
      {
        header: notesHeader,
        key: 'details',
        thProps: { style: { width: '190px', minWidth: '190px', maxWidth: '190px' } },
        tdProps: { style: { width: '190px', minWidth: '190px', maxWidth: '190px', fontSize: '12px' } },
        render: (entry: any) => {
          const notes = [];

          // Check for settlement status first
          if (entry.deal_status === 'settlement_free_agent_offer') {
            // Get team name for the signing team
            const signingTeam = entry.dealData?.teams && entry.dealData.teams.length > 0
              ? getTeamNameById(entry.dealData.teams[0], myLeague)
              : 'Unknown Team';

            notes.push(
              <div key="settlement" style={{ marginBottom: '4px' }}>
                <div style={{ color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }}>
                  Signed by {signingTeam}
                </div>
                <div style={{ color: '#48bb78' }}>
                  Settlement closes at midnight
                </div>
              </div>
            );
          } else if (entry.deal_status === 'settlement_waiver_claim') {
            // Get team name for the claiming team
            const claimingTeam = entry.dealData?.teams && entry.dealData.teams.length > 0
              ? getTeamNameById(entry.dealData.teams[0], myLeague)
              : 'Unknown Team';

            notes.push(
              <div key="settlement" style={{ marginBottom: '4px' }}>
                <div style={{ color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }}>
                  Claimed by {claimingTeam}
                </div>
                <div style={{ color: '#48bb78' }}>
                  Settlement closes at midnight
                </div>
              </div>
            );
          }
          // Add bid information if dealData has bids and not in settlement
          else if (entry.dealData && entry.dealData.bids > 0) {
            const offerText = entry.dealData.bids === 1 ? '1 offer' : `${entry.dealData.bids} offers`;
            const offerVerb = entry.dealData.bids === 1 ? 'is' : 'are';
            const checkMarks = '✓'.repeat(entry.dealData.bids);
            notes.push(
              <div key="bids" style={{ marginBottom: '4px' }}>
                {offerText} made&nbsp;&nbsp;<span style={{ color: '#48bb78' }}>{checkMarks}</span>
              </div>
            );

            // Check if my team has made an offer
            const myTeamOfferInfo = getMyTeamOfferInfo(entry);
            if (myTeamOfferInfo.hasOffer) {
              let isWinning = false;
              if (entry.dealData?.offers && entry.dealData.offers.length > 0) {
                const highestOfferScore = entry.dealData.offers.reduce((max: number, offer: any) =>
                  Math.max(max, offer.offer?.offerScore || 0), 0);
                const myOffer = entry.dealData.offers.find((offer: any) => Number(offer.team) === Number(myTeam));
                if (myOffer && myOffer.offer?.offerScore && Math.abs(myOffer.offer.offerScore - highestOfferScore) < 0.001 && highestOfferScore > 0) {
                  isWinning = true;
                }
              }

              notes.push(
                <div key="my-team-offer" className="dominus-blue" style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                  Your team submitted an offer on {myTeamOfferInfo.date}
                  {isWinning && user?.systemSettings?.seasonStatus !== "In Season" && (
                    <span style={{ color: '#ff9b21', marginLeft: '0px', display: 'block' }}>
                      Current High Bidder
                    </span>
                  )}
                </div>
              );
            }

            // Display Top Offer Score if available
            if (entry.dealData.offerScore && user?.systemSettings?.seasonStatus !== "In Season") {
              const score = (entry.dealData.offerScore / 1000000).toFixed(2);
              notes.push(
                <div key="offer-score" style={{ marginBottom: '4px', fontSize: '13px', fontStyle: 'italic', color: '#718096' }}>
                  Top Offer Score: {score}
                </div>
              );
            }

            if (entry.dealData.expires) {
              notes.push(
                <div key="expires" style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                  {getPstTimeDifference(entry.dealData.expires) > 0 ? (
                    <>
                      <span
                        style={{
                          color: '#48bb78',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleUserAction(entry, 'sign', 'MLB');
                        }}
                      >
                        Accepting offers
                      </span> until {formatPstDateTime(entry.dealData.expires, true)}
                    </>
                  ) : (
                    <span style={{ color: '#e53e3e', fontWeight: 'bold' }}>
                      No longer accepting offers as of {formatPstDateTime(entry.dealData.expires, true)}
                    </span>
                  )}
                </div>
              );
            }
          }

          // Add claims information for waiver wire players (but not if in settlement)
          if (entry.dealData && entry.dealData.claims && entry.dealData.claims > 0 && entry.deal_status !== 'settlement_waiver_claim') {
            const claimsText = entry.dealData.claims === 1 ? '1 claim' : `${entry.dealData.claims} claims`;
            const checkMarks = '✓'.repeat(Math.min(entry.dealData.claims, 5));
            notes.push(
              <div key="claims" style={{ marginBottom: '4px' }}>
                {claimsText} submitted&nbsp;&nbsp;<span style={{ color: '#48bb78' }}>{checkMarks}</span>
              </div>
            );
          }

          // Add existing trade status information (for free agents) or deal status (for waiver wire)
          if (entry.trade_status_until) {
            notes.push(
              <div key="claims" style={{ fontStyle: 'italic', fontSize: '0.95em', marginTop: notes.length > 0 ? '4px' : '0' }}>
                Claims through<br />{formatPstDateTime(entry.trade_status_until)}
              </div>
            );
          } else if (entry.deal_until && entry.deal_status !== 'free_agent_offer' && entry.deal_status !== 'settlement_free_agent_offer' && entry.deal_status !== 'settlement_waiver_claim') {
            // Show waivers expire message (skip free agent offers since "Accepting offers until" already shows the time)
            notes.push(
              <div key="deal-until" style={{ fontStyle: 'italic', fontSize: '0.95em', marginTop: notes.length > 0 ? '4px' : '0' }}>
                Waivers expire<br />{formatPstDateTime(entry.deal_until)}
              </div>
            );
          }

          return notes.length > 0 ? (
            <div style={{ textAlign: 'left', display: 'block' }}>
              {notes}
            </div>
          ) : null;
        },
      },
      {
        header: 'POS (B/T)',
        key: 'pos_bt',
        thProps: { style: { width: '130px', minWidth: '130px', maxWidth: '130px' } },
        tdProps: { style: { width: '130px', minWidth: '130px', maxWidth: '130px' } },
        render: (entry: any) => {
          const positionsText = positionsFromRwSeason(entry);
          const bats = entry.rwPlayer.bats || '-';
          const throws = entry.rwPlayer.throws || '-';
          const btText = `${bats}${throws ? '/' + throws : ''}`;

          return (
            <VStack align="flex-start" spacing={1}>
              <Text>{positionsText}</Text>
              <Text fontSize="xs" color="gray.400">{btText}</Text>
            </VStack>
          );
        },
      },
      {
        header: 'MLS',
        key: 'mls',
        thProps: { style: { minWidth: '80px' } },
        tdProps: { style: { minWidth: '80px' } },
        render: (entry: any) => {
          const mls = entry.rwPlayer.mls;
          if (typeof mls === 'number' && !isNaN(mls)) return mls.toFixed(3);
          return '-';
        },
      },
      {
        header: 'Options',
        key: 'options',
        thProps: { textAlign: 'center', style: { minWidth: '80px' } },
        tdProps: { textAlign: 'center', style: { minWidth: '80px' } },
        render: (entry: any) => {
          const options = entry.additionalInfo?.options ?? entry.rwPlayer.options;
          if (options === null || options === undefined || options === '' || isNaN(Number(options))) {
            return '-';
          }
          return options;
        },
      },
      {
        header: 'Opt. Remaining',
        key: 'options_remaining',
        thProps: { textAlign: 'center', style: { minWidth: '110px' } },
        tdProps: { textAlign: 'center', style: { minWidth: '110px' } },
        render: (entry: any) => {
          const optionsRemaining = (entry as any).options_remaining;
          if (optionsRemaining != null && optionsRemaining !== '' && !isNaN(Number(optionsRemaining)) && Number(optionsRemaining) >= 0) {
            return optionsRemaining;
          }
          return '-';
        },
      },
      {
        header: 'Prior OR',
        key: 'prior_or',
        thProps: { textAlign: 'center', style: { minWidth: '90px' } },
        tdProps: { textAlign: 'center', style: { minWidth: '90px' } },
        render: (entry: any) => {
          const priorOrYes = ((entry.additionalInfo?.prior_or ?? entry.rwPlayer.prior_or) || '').toLowerCase() === 'yes';
          const mlsVal = entry.additionalInfo?.mls ?? entry.rwPlayer.mls;
          const mlsEligible = typeof mlsVal === 'number' && !isNaN(mlsVal) && mlsVal >= 5;
          return (priorOrYes || mlsEligible) ? 'YES' : <Text fontSize="sm" color="gray.400">no</Text>;
        },
      },
      {
        header: 'Age',
        key: 'age',
        thProps: { style: { minWidth: '70px' } },
        tdProps: { style: { minWidth: '70px' } },
        render: (entry: any) => {
          if (entry.rwPlayer.birth_date) {
            const birthDate = new Date(entry.rwPlayer.birth_date);
            const age = CURRENT_YEAR - birthDate.getFullYear();
            return age;
          }
          return '-';
        },
      },
      {
        header: 'Salary',
        key: 'salary',
        render: (entry: any) => formatSalary(entry.playerContract?.current_salary, false),
      },
      {
        header: '',
        key: 'claim',
        render: (entry: any, isWaiverWireView: boolean) => {
          if (isWaiverWireView) {
            // Check if the player's current team is my team
            const isMyTeamPlayer = myTeam && entry.team_id && Number(entry.team_id) === Number(myTeam);

            if (isMyTeamPlayer) {
              return null; // Don't show any button for players on my team
            }

            // Check if my team has already claimed this player
            const hasMyTeamClaimed = entry.dealData?.teams && myTeam &&
              entry.dealData.teams.some((teamId: any) => Number(teamId) === Number(myTeam));

            if (hasMyTeamClaimed) {
              return (
                <Text className="dominus-blue" style={{ marginRight: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                  Your team has submitted a claim
                </Text>
              );
            }

            return (
              <Button style={{ marginRight: '12px' }} size="sm" colorScheme="blue" isDisabled={entry.deal_status === 'settlement_waiver_claim'} onClick={e => { e.stopPropagation(); if (typeof entry.handleClaimWaiver === 'function') entry.handleClaimWaiver(entry); }}>
                Claim
              </Button>
            );
          } else {
            return renderMakeOfferButton(entry);
          }
        },
      },
    ],
    'Contract Details': [
      {
        header: 'Player',
        key: 'player',
        render: (entry: any) => renderPlayerCell(entry, false),
      },
      {
        header: notesHeader,
        key: 'details',
        thProps: { style: { width: '190px', minWidth: '190px', maxWidth: '190px' } },
        tdProps: { style: { width: '190px', minWidth: '190px', maxWidth: '190px', fontSize: '12px' } },
        render: (entry: any) => {
          const notes = [];

          // Check for settlement status first
          if (entry.deal_status === 'settlement_free_agent_offer') {
            // Get team name for the signing team
            const signingTeam = entry.dealData?.teams && entry.dealData.teams.length > 0
              ? getTeamNameById(entry.dealData.teams[0], myLeague)
              : 'Unknown Team';

            notes.push(
              <div key="settlement" style={{ marginBottom: '4px' }}>
                <div style={{ color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }}>
                  Signed by {signingTeam}
                </div>
                <div style={{ color: '#48bb78' }}>
                  Settlement closes at midnight
                </div>
              </div>
            );
          } else if (entry.deal_status === 'settlement_waiver_claim') {
            // Get team name for the claiming team
            const claimingTeam = entry.dealData?.teams && entry.dealData.teams.length > 0
              ? getTeamNameById(entry.dealData.teams[0], myLeague)
              : 'Unknown Team';

            notes.push(
              <div key="settlement" style={{ marginBottom: '4px' }}>
                <div style={{ color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }}>
                  Claimed by {claimingTeam}
                </div>
                <div style={{ color: '#48bb78' }}>
                  Settlement closes at midnight
                </div>
              </div>
            );
          }
          // Add bid information if dealData has bids and not in settlement
          else if (entry.dealData && entry.dealData.bids > 0) {
            const offerText = entry.dealData.bids === 1 ? '1 offer' : `${entry.dealData.bids} offers`;
            const offerVerb = entry.dealData.bids === 1 ? 'is' : 'are';
            const checkMarks = '✓'.repeat(entry.dealData.bids);
            notes.push(
              <div key="bids" style={{ marginBottom: '4px' }}>
                {offerText} made&nbsp;&nbsp;<span style={{ color: '#48bb78' }}>{checkMarks}</span>
              </div>
            );

            // Check if my team has made an offer
            const myTeamOfferInfo = getMyTeamOfferInfo(entry);
            if (myTeamOfferInfo.hasOffer) {
              let isWinning = false;
              if (entry.dealData?.offers && entry.dealData.offers.length > 0) {
                const highestOfferScore = entry.dealData.offers.reduce((max: number, offer: any) =>
                  Math.max(max, offer.offer?.offerScore || 0), 0);
                const myOffer = entry.dealData.offers.find((offer: any) => Number(offer.team) === Number(myTeam));
                if (myOffer && myOffer.offer?.offerScore && Math.abs(myOffer.offer.offerScore - highestOfferScore) < 0.001 && highestOfferScore > 0) {
                  isWinning = true;
                }
              }

              notes.push(
                <div key="my-team-offer" className="dominus-blue" style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                  Your team submitted an offer on {myTeamOfferInfo.date}
                  {isWinning && user?.systemSettings?.seasonStatus !== "In Season" && (
                    <span style={{ color: '#ff9b21', marginLeft: '0px', display: 'block' }}>
                      Current High Bidder
                    </span>
                  )}
                </div>
              );
            }

            if (entry.dealData.expires) {
              notes.push(
                <div key="expires" style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                  <span
                    style={{
                      color: '#48bb78',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleUserAction(entry, 'sign', 'MLB');
                    }}
                  >
                    Accepting offers
                  </span> until {formatPstDateTime(entry.dealData.expires)}
                </div>
              );
            }
          }

          // Add claims information for waiver wire players (but not if in settlement)
          if (entry.dealData && entry.dealData.claims && entry.dealData.claims > 0 && entry.deal_status !== 'settlement_waiver_claim') {
            const claimsText = entry.dealData.claims === 1 ? '1 claim' : `${entry.dealData.claims} claims`;
            const checkMarks = '✓'.repeat(Math.min(entry.dealData.claims, 5));
            notes.push(
              <div key="claims" style={{ marginBottom: '4px' }}>
                {claimsText} submitted&nbsp;&nbsp;<span style={{ color: '#48bb78' }}>{checkMarks}</span>
              </div>
            );
          }

          // Add existing trade status information (for free agents) or deal status (for waiver wire)
          if (entry.trade_status_until) {
            notes.push(
              <div key="claims" style={{ fontStyle: 'italic', fontSize: '0.95em', marginTop: notes.length > 0 ? '4px' : '0' }}>
                Claims through<br />{formatPstDateTime(entry.trade_status_until)}
              </div>
            );
          } else if (entry.deal_until && entry.deal_status !== 'free_agent_offer' && entry.deal_status !== 'settlement_free_agent_offer' && entry.deal_status !== 'settlement_waiver_claim') {
            // Show waivers expire message (skip free agent offers since "Accepting offers until" already shows the time)
            notes.push(
              <div key="deal-until" style={{ fontStyle: 'italic', fontSize: '0.95em', marginTop: notes.length > 0 ? '4px' : '0' }}>
                Waivers expire<br />{formatPstDateTime(entry.deal_until)}
              </div>
            );
          }

          return notes.length > 0 ? (
            <div style={{ textAlign: 'left', display: 'block' }}>
              {notes}
            </div>
          ) : null;
        },
      },
      {
        header: 'POS',
        key: 'pos',
        render: (entry: any) => positionsFromRwSeason(entry),
      },
      {
        header: 'Year Signed',
        key: 'year_signed',
        thProps: { style: { minWidth: '100px' } },
        tdProps: { style: { minWidth: '100px' } },
        render: (entry: any) => entry.playerContract?.year_signed || '-',
      },
      {
        header: 'Current Salary',
        key: 'current_salary',
        thProps: { style: { minWidth: '140px' } },
        tdProps: { style: { minWidth: '140px' } },
        render: (entry: any) => formatSalary(entry.playerContract?.current_salary, false),
      },
      {
        header: 'Total Contract',
        key: 'total_contract',
        thProps: { style: { minWidth: '150px' } },
        tdProps: { style: { minWidth: '150px' } },
        render: (entry: any) => {
          const val = entry.playerContract?.total_value;
          let totalValueNum: number | null = null;
          if (typeof val === 'string') totalValueNum = parseFloat(val);
          else if (typeof val === 'number') totalValueNum = val;
          if (totalValueNum !== null && !isNaN(totalValueNum)) return `$${totalValueNum.toLocaleString()}`;
          if (entry.playerContract?.salaries && entry.playerContract.salaries.length > 0) {
            const total = entry.playerContract.salaries.reduce((sum: number, s: { salary: number | string | undefined }) => {
              let salaryNum = 0;
              if (typeof s.salary === 'string') salaryNum = parseFloat(s.salary);
              else if (typeof s.salary === 'number') salaryNum = s;
              return sum + (isNaN(salaryNum) ? 0 : salaryNum);
            }, 0);
            return `$${total.toLocaleString()}`;
          }
          return '-';
        },
      },
      {
        header: 'Contract Type',
        key: 'contract_type',
        thProps: { style: { minWidth: '130px' } },
        tdProps: { style: { minWidth: '130px' } },
        render: (entry: any) => {
          if (entry.playerContract?.contract_types && entry.playerContract.contract_types.length > 0) {
            const currentYear = CURRENT_YEAR;
            const currentContract = entry.playerContract.contract_types.find((ct: any) => ct.year === currentYear);
            return currentContract?.type || '-';
          }
          return '-';
        },
      },
      {
        header: 'Buyout',
        key: 'buyout',
        thProps: { style: { minWidth: '90px' } },
        tdProps: { style: { minWidth: '90px' } },
        render: (entry: any) => {
          if (entry.playerContract?.buyouts && entry.playerContract.buyouts.length > 0) {
            const currentYear = CURRENT_YEAR;
            const currentBuyout = entry.playerContract.buyouts.find((b: any) => b.year === currentYear);
            if (currentBuyout) return `$${currentBuyout.amount.toLocaleString()}`;
          }
          return '-';
        },
      },
      {
        header: '',
        key: 'claim',
        render: (entry: any, isWaiverWireView: boolean) => {
          if (isWaiverWireView) {
            // Check if the player's current team is my team
            const isMyTeamPlayer = myTeam && entry.team_id && Number(entry.team_id) === Number(myTeam);

            if (isMyTeamPlayer) {
              return null; // Don't show any button for players on my team
            }

            // Check if my team has already claimed this player
            const hasMyTeamClaimed = entry.dealData?.teams && myTeam &&
              entry.dealData.teams.some((teamId: any) => Number(teamId) === Number(myTeam));

            if (hasMyTeamClaimed) {
              return (
                <Text className="dominus-blue" style={{ marginRight: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                  Your team has submitted a claim
                </Text>
              );
            }

            return (
              <Button style={{ marginRight: '12px' }} size="sm" colorScheme="blue" isDisabled={entry.deal_status === 'settlement_waiver_claim'} onClick={e => { e.stopPropagation(); if (typeof entry.handleClaimWaiver === 'function') entry.handleClaimWaiver(entry); }}>
                Claim
              </Button>
            );
          } else {
            return renderMakeOfferButton(entry);
          }
        },
      },
    ],
  };

  const columns = freeAgentColumnsConfig[tabName] || freeAgentColumnsConfig['Players'];
  const barBg = "#222";
  const barText = '#fff';
  const barBorder = 'transparent';

  // Free agent team object for modal
  const freeAgentTeam = {
    id: 'free-agent',
    name: 'Free Agent',
    logo: '', // Optionally use a generic icon
    color_primary: '#888',
    color_secondary: '#bbb',
    color_text: '#fff',
  };

  // Modal and selected player state
  const [selectedPlayer, setSelectedPlayer] = React.useState<any | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleRowClick = (entry: any) => {
    setSelectedPlayer(entry);
    onOpen();
  };

  // Check if waiver claim would exceed salary cap and calculate excess amount
  const checkSalaryCapExceedance = async (playerSalary: number): Promise<{ exceeds: boolean; excessAmount: number }> => {
    if (!myTeam || !currentTeamData?.salary_cap || !currentPayroll || !myLeague?.id) {
      return { exceeds: false, excessAmount: 0 };
    }

    // Get the max salary change from all outstanding deals for this team
    const response = await apiClient.get<{ positiveChange: boolean; positiveChangeAmount: number }>(
      `/deals/getMaxSalaryChangeFromDealsForTeam/${myTeam}/${myLeague.id}`
    );
    const { positiveChange, positiveChangeAmount } = response.data;

    // Calculate the worst-case payroll scenario:
    // - If outstanding deals have a positive max change (increasing payroll), add it
    // - Then add this player's salary on top
    const outstandingDealsImpact = positiveChange ? positiveChangeAmount : 0;
    const newPayroll = currentPayroll + outstandingDealsImpact + playerSalary;

    // Salary cap is in millions, convert to actual dollars
    const salaryCapInDollars = Number(currentTeamData.salary_cap) * 1000000;

    // Check if new payroll exceeds salary cap
    if (newPayroll > salaryCapInDollars) {
      const excessAmount = newPayroll - salaryCapInDollars;
      return { exceeds: true, excessAmount };
    }

    return { exceeds: false, excessAmount: 0 };
  };

  // Actual claim submission logic (extracted for reuse)
  // Accept optional paymentIntentId parameter to avoid race condition with state updates
  const submitClaimInternal = async (entry: any, overridePaymentIntentId?: string | null) => {
    const teamId = myTeam; // Use my team ID for claiming, not the player's current team
    const playerId = entry.id; // This is the roster ID from dom_rosters table
    if (!playerId) {
      toast.error('Error', 'Missing player ID');
      return;
    }
    if (!teamId) {
      toast.error('Error', 'Missing team ID');
      return;
    }
    if (!myLeague?.id) {
      toast.error('Error', 'League information not available');
      return;
    }

    // Use overridePaymentIntentId if provided, otherwise fall back to state
    const effectivePaymentIntentId = overridePaymentIntentId !== undefined ? overridePaymentIntentId : paymentIntentId;

    try {
      // Make API call directly to include paymentIntentId in the request
      const requestBody: any = {
        teamId,
        leagueId: myLeague.id
      };

      // Add paymentIntentId to request if available (for linking)
      if (effectivePaymentIntentId) {
        requestBody.paymentIntentId = effectivePaymentIntentId;
      }

      console.log('Submitting waiver claim with paymentIntentId:', effectivePaymentIntentId);
      const response = await apiClient.post(`/deals/claim_waiver/${playerId}`, requestBody);
      const newDeal = response.data;
      console.log('Waiver claim response:', newDeal);

      if (newDeal) {
        // For new waiver claims, update PaymentIntent metadata with dealId after creation
        // This creates a two-way link: deal has paymentIntentId, PaymentIntent has dealId
        // Note: Backend must support POST /store/update-payment-intent-metadata endpoint
        // Alternative: Backend could handle this automatically when creating deals with paymentIntentId
        if (effectivePaymentIntentId && newDeal.id) {
          try {
            console.log('Updating PaymentIntent metadata for new waiver claim deal:', { paymentIntentId: effectivePaymentIntentId, dealId: newDeal.id });
            await apiClient.post('/store/update-payment-intent-metadata', {
              paymentIntentId: effectivePaymentIntentId,
              metadata: { dealId: String(newDeal.id) }
            });
            console.log('Successfully linked PaymentIntent to new waiver claim deal:', { paymentIntentId: effectivePaymentIntentId, dealId: newDeal.id });
          } catch (error) {
            console.error('Failed to update PaymentIntent metadata with dealId:', error);
            // Don't fail the deal creation if metadata update fails - the deal still has paymentIntentId
          }
        } else {
          console.warn('Skipping PaymentIntent metadata update:', {
            hasPaymentIntentId: !!effectivePaymentIntentId,
            hasDealId: !!newDeal.id,
            paymentIntentId: effectivePaymentIntentId,
            dealId: newDeal.id
          });
        }

        toast.success('Success', `Claim submitted for ${entry.rwPlayer?.first_name} ${entry.rwPlayer?.last_name}!`);
        setPaymentIntentId(null); // Clear after successful linking

        // Refresh deals after claiming
        if (refetch) {
          refetch();
        }
      } else {
        toast.error('Error', 'Failed to claim player - no deal returned');
      }
    } catch (error) {
      toast.error('Error', `Failed to claim player: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handler for claim button in table (same as PlayerCard)
  const handleClaimWaiver = async (entry: any) => {
    // Get player's salary
    const playerSalary = Number(entry.playerContract?.current_salary || 0);

    // Check if claim would exceed salary cap
    const { exceeds, excessAmount } = await checkSalaryCapExceedance(playerSalary);

    if (exceeds) {
      // Store the submission function and show pre-approval modal
      setPendingClaimSubmission(() => () => submitClaimInternal(entry));
      setPendingClaimPlayer(entry);
      setPreApprovalAmount(excessAmount);
      setIsPreApprovalOpen(true);
      return;
    }

    // No salary cap issue, proceed with claim
    await submitClaimInternal(entry);
  };

  // Handle pre-approval success
  const handlePreApprovalSuccess = async (result: any) => {
    const { paymentIntent, paymentIntentId: piId } = result;
    console.log('Pre-approval successful, submitting claim...', { paymentIntent, paymentIntentId: piId });

    // Store paymentIntentId for linking with deal
    if (piId) {
      setPaymentIntentId(piId);

      // Update purchase status to pending
      try {
        await apiClient.put(`/purchases/${piId}/status/pending`);
        console.log('Purchase status updated to pending:', piId);
      } catch (error) {
        console.error('Error updating purchase status to pending:', error);
        // Don't fail the entire flow if purchase update fails
      }
    }

    // Close the pre-approval modal
    setIsPreApprovalOpen(false);

    // Proceed with claim submission - pass paymentIntentId directly to avoid race condition
    if (pendingClaimSubmission && pendingClaimPlayer) {
      try {
        console.log('Calling pending claim submission with paymentIntentId:', piId);
        // Pass paymentIntentId directly to avoid race condition with state update
        await submitClaimInternal(pendingClaimPlayer, piId);
        console.log('Claim submitted successfully after pre-approval');
        setPendingClaimSubmission(null);
        setPendingClaimPlayer(null);
      } catch (error) {
        console.error('Error submitting claim after pre-approval:', error);
        // Error is already handled in submitClaimInternal, but we should clear the pending submission
        setPendingClaimSubmission(null);
        setPendingClaimPlayer(null);
      }
    } else {
      console.warn('No pending claim submission found after pre-approval success');
      toast.error('Error', 'Claim submission failed - no pending submission found');
    }
  };

  // Handle pre-approval failure/close
  const handlePreApprovalClose = async () => {
    // If paymentIntentId exists, a purchase was created and should be deleted
    if (paymentIntentId) {
      try {
        await apiClient.delete(`/purchases/${paymentIntentId}`);
        console.log('Purchase deleted:', paymentIntentId);
      } catch (error) {
        console.error('Error deleting purchase:', error);
        // Don't show error to user, just log it
      }
    }

    setIsPreApprovalOpen(false);
    setPendingClaimSubmission(null);
    setPendingClaimPlayer(null);
    setPaymentIntentId(null); // Clear paymentIntentId on close
    toast.error('Error', 'Payment authorization is required to proceed with this claim. Please adjust your roster to stay within your salary cap or authorize the payment.');
  };

  const [signModalDealId, setSignModalDealId] = useState<number | null>(null);
  const [signModalInitialSalaries, setSignModalInitialSalaries] = useState<number[] | undefined>(undefined);

  // Generic handler for user actions
  const handleUserAction = (player: any, action: string, detail?: any) => {
    if (action === 'make_offer' && (!seasonStatus || (seasonStatus !== "Off Season" && seasonStatus !== "In Season"))) {
      toast.error('Warning', 'You cannot make offers at this time. Offers will be accepted after the first of the year');
      return;
    }

    // Check for existing offer from my team
    let existingDealId = null;
    let existingSalaries: number[] | undefined = undefined;

    if (myTeam && player.dealData && player.dealData.offers) {
      console.log(player)
      const myTeamOffer = player.dealData.offers.find((offer: any) => Number(offer.team) === Number(myTeam));
      console.log(myTeamOffer)
      if (myTeamOffer) {
        existingDealId = player.dealData.dealId || null; // Handle missing deal_id
        if (myTeamOffer.offer && myTeamOffer.offer.yearlySalaries) {
          existingSalaries = myTeamOffer.offer.yearlySalaries;
        }
      }
    }

    if (action === 'sign') {
      setSignModalPlayer(player);
      setSignModalLevel(detail);
      setSignModalDealId(existingDealId);
      setSignModalInitialSalaries(existingSalaries);
      setIsSignModalOpen(true);
    } else if (action === 'make_offer') {
      setSignModalPlayer(player);
      setSignModalLevel('MLB'); // Default to MLB level for free agent offers
      setSignModalDealId(existingDealId);
      setSignModalInitialSalaries(existingSalaries);
      setIsSignModalOpen(true);
    } else if (action === 'trade') {
      // Handle trade action - could open trade modal or redirect
      console.log('Trade requested for player:', player);
      // TODO: Implement trade handling
    }
    // Add more action handlers here as needed
  };

  // Attach handler to each player row
  const tablePlayers = tabName === 'Players'
    ? players.map(p => ({ ...p, handleClaimWaiver }))
    : players;

  // Split header/body: sticky + perfect alignment via measured widths
  const headerXRef = useRef<HTMLDivElement>(null);
  const bodyXRef = useRef<HTMLDivElement>(null);

  // Update gradient on mount, scroll (window + table), and resize
  useEffect(() => {
    // Sync sticky gradient slice to match page background
    const syncStickyGradientSlice = () => {
      // Handle table sticky surfaces (unchanged behavior)
      const tableStickyEls = document.querySelectorAll<HTMLElement>('[data-sticky-surface="1"]');

      // Handle tabs sticky surfaces (only when stuck)
      const tabsStickyEls = document.querySelectorAll<HTMLElement>('[data-sticky-tabs-surface="1"][data-tabs-stuck="1"]');

      // Clear styles for tabs that are NOT stuck
      const tabsNotStuckEls = document.querySelectorAll<HTMLElement>('[data-sticky-tabs-surface="1"]:not([data-tabs-stuck="1"])');
      tabsNotStuckEls.forEach((el) => {
        el.style.backgroundColor = '';
        el.style.backgroundImage = '';
        el.style.backgroundRepeat = '';
        el.style.backgroundSize = '';
        el.style.backgroundPosition = '';
        el.style.boxShadow = '';
      });

      // Compute shared background canvas size
      const bgW = document.documentElement.clientWidth;
      const bgH = document.documentElement.scrollHeight;

      // Apply styles to table sticky surfaces (unchanged)
      tableStickyEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const pageX = window.scrollX + rect.left;
        const pageY = window.scrollY + rect.top;

        // Apply inline styles directly
        el.style.backgroundColor = '#002e0b';
        el.style.backgroundImage = PAGE_GRADIENT;
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundSize = `${bgW}px ${bgH}px`;
        el.style.backgroundPosition = `${-pageX}px ${-pageY}px`;
        el.style.boxShadow = FROST_WASH;
      });

      // Apply styles to tabs sticky surfaces (only when stuck)
      tabsStickyEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const pageX = window.scrollX + rect.left;
        const pageY = window.scrollY + rect.top;

        // Apply inline styles directly
        el.style.backgroundColor = '#002e0b';
        el.style.backgroundImage = PAGE_GRADIENT;
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundSize = `${bgW}px ${bgH}px`;
        el.style.backgroundPosition = `${-pageX}px ${-pageY}px`;
        el.style.boxShadow = FROST_WASH;
      });
    };

    // Initial update after first paint
    const rafId = requestAnimationFrame(() => {
      syncStickyGradientSlice();
    });

    // Throttle with requestAnimationFrame
    let pendingRaf: number | null = null;
    const handleUpdate = () => {
      if (pendingRaf === null) {
        pendingRaf = requestAnimationFrame(() => {
          syncStickyGradientSlice();
          pendingRaf = null;
        });
      }
    };

    // Listen to window scroll
    window.addEventListener('scroll', handleUpdate, { passive: true });
    // Listen to body scroll container scroll
    const bodyScrollEl = bodyXRef.current;
    if (bodyScrollEl) {
      bodyScrollEl.addEventListener('scroll', handleUpdate, { passive: true });
    }
    // Listen to window resize
    window.addEventListener('resize', handleUpdate, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      // Check ref in cleanup in case it changed
      const currentScrollEl = bodyXRef.current;
      if (currentScrollEl) {
        currentScrollEl.removeEventListener('scroll', handleUpdate);
      }
      if (pendingRaf !== null) {
        cancelAnimationFrame(pendingRaf);
      }
    };
  }, [tablePlayers.length, tabName]); // Re-sync when table data or tab changes


  // Re-run sync after React renders that may recreate Th/Td nodes
  useLayoutEffect(() => {
    const syncStickyGradientSlice = () => {
      // Handle table sticky surfaces (unchanged behavior)
      const tableStickyEls = document.querySelectorAll<HTMLElement>('[data-sticky-surface="1"]');

      // Handle tabs sticky surfaces (only when stuck)
      const tabsStickyEls = document.querySelectorAll<HTMLElement>('[data-sticky-tabs-surface="1"][data-tabs-stuck="1"]');

      // Clear styles for tabs that are NOT stuck
      const tabsNotStuckEls = document.querySelectorAll<HTMLElement>('[data-sticky-tabs-surface="1"]:not([data-tabs-stuck="1"])');
      tabsNotStuckEls.forEach((el) => {
        el.style.backgroundColor = '';
        el.style.backgroundImage = '';
        el.style.backgroundRepeat = '';
        el.style.backgroundSize = '';
        el.style.backgroundPosition = '';
        el.style.boxShadow = '';
      });

      const bgW = document.documentElement.clientWidth;
      const bgH = document.documentElement.scrollHeight;

      // Apply styles to table sticky surfaces (unchanged)
      tableStickyEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const pageX = window.scrollX + rect.left;
        const pageY = window.scrollY + rect.top;

        el.style.backgroundColor = '#002e0b';
        el.style.backgroundImage = PAGE_GRADIENT;
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundSize = `${bgW}px ${bgH}px`;
        el.style.backgroundPosition = `${-pageX}px ${-pageY}px`;
        el.style.boxShadow = FROST_WASH;
      });

      // Apply styles to tabs sticky surfaces (only when stuck)
      tabsStickyEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const pageX = window.scrollX + rect.left;
        const pageY = window.scrollY + rect.top;

        el.style.backgroundColor = '#002e0b';
        el.style.backgroundImage = PAGE_GRADIENT;
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundSize = `${bgW}px ${bgH}px`;
        el.style.backgroundPosition = `${-pageX}px ${-pageY}px`;
        el.style.boxShadow = FROST_WASH;
      });
    };

    const rafId = requestAnimationFrame(() => {
      syncStickyGradientSlice();
    });
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [tablePlayers.length, tabName, columns.length]);

  // scroll sync
  useEffect(() => {
    const bodyEl = bodyXRef.current;
    const headerEl = headerXRef.current;
    if (!bodyEl || !headerEl) return;

    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          headerEl.scrollLeft = bodyEl.scrollLeft;
          rafId = null;
        });
      }
    };

    bodyEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      bodyEl.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // width sync
  const syncHeaderColumnWidths = React.useCallback(() => {
    const bodyContainer = bodyXRef.current;
    const bodyTable = bodyContainer?.querySelector<HTMLTableElement>('table');
    const headerTable = headerXRef.current?.querySelector<HTMLTableElement>('table');
    if (!bodyTable || !headerTable) return;

    // Find first visible body row
    const bodyRows = bodyTable.querySelectorAll<HTMLTableRowElement>('tbody tr');
    if (bodyRows.length === 0) return;

    const firstRow = bodyRows[0];
    const bodyCells = firstRow.querySelectorAll<HTMLTableCellElement>('td[data-col-key]');

    // Measure each body cell and apply to matching header cell
    bodyCells.forEach((bodyCell) => {
      const colKey = bodyCell.getAttribute('data-col-key');
      if (!colKey) return;

      const width = bodyCell.getBoundingClientRect().width;
      const headerTh = headerTable.querySelector<HTMLTableCellElement>(`th[data-col-key="${colKey}"]`);
      if (headerTh) {
        headerTh.style.width = `${width}px`;
        headerTh.style.minWidth = `${width}px`;
        headerTh.style.maxWidth = `${width}px`;
      }
    });

    // Sync table width
    const bodyScrollWidth = bodyTable.scrollWidth;
    headerTable.style.width = `${bodyScrollWidth}px`;
    headerTable.style.minWidth = `${bodyScrollWidth}px`;
  }, []);

  // Call width sync after render and on changes
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      syncHeaderColumnWidths();
    });
    return () => cancelAnimationFrame(rafId);
  }, [tablePlayers.length, tabName, columns.length, syncHeaderColumnWidths]);

  // ResizeObserver for body table changes
  useEffect(() => {
    const bodyContainer = bodyXRef.current;
    const bodyTable = bodyContainer?.querySelector<HTMLTableElement>('table');
    if (!bodyTable) return;

    const resizeObserver = new ResizeObserver(() => {
      syncHeaderColumnWidths();
    });

    resizeObserver.observe(bodyTable);
    return () => resizeObserver.disconnect();
  }, [syncHeaderColumnWidths]);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      syncHeaderColumnWidths();
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [syncHeaderColumnWidths]);

  return (
    <Box mb={8} mt={2}>
      {/* <Flex
        align="center"
        bg={barBg}
        color={barText}
        border={barBorder !== 'transparent' ? '1.5px solid' : undefined}
        borderColor={barBorder}
        px={4}
        py={2}
        borderRadius="md"
        fontWeight="bold"
        fontSize="lg"
        mb={2}
      >
        Free Agents
      </Flex> */}
      <Box>
        {/* Split header/body: sticky + perfect alignment via measured widths */}
        {/* Sticky header wrapper */}
        <Box
          position="sticky"
          top="var(--sticky-tabs-height, 0px)"
          zIndex={20}
        >
          <Box
            ref={headerXRef}
            overflowX="hidden"
            overflowY="visible"
          >
            <Table
              variant="simple"
              size="sm"
              width="100%"
              sx={{ borderCollapse: "separate", borderSpacing: 0 }}
            >
              <Thead
                sx={{
                  th: {
                    wordBreak: "keep-all",
                    overflowWrap: "normal",
                    hyphens: "manual",
                  },
                }}
              >
                <Tr>
                  {columns.map((col, index) => (
                    <Th
                      key={col.key}
                      data-col-key={col.key}
                      data-sticky-surface="1"
                      {...(index === 0 ? { position: "sticky", left: "0", zIndex: 21 } : {})}
                      {...(col.thProps || {})}
                      bg={stickyBg}
                    >
                      {col.header}
                    </Th>
                  ))}
                </Tr>
              </Thead>
            </Table>
          </Box>
        </Box>
        {/* Body wrapper */}
        <Box
          maxW="100%"
          borderRadius="md"
          bg="whiteAlpha.100"
        >
          <Box
            ref={bodyXRef}
            maxW="100%"
            overflowX="auto"
          >
            <Table
              variant="simple"
              size="sm"
              width="100%"
              sx={{ borderCollapse: "separate", borderSpacing: 0 }}
            >
              <Tbody>
                {tablePlayers.length === 0 ? (
                  <Tr>
                    <Td colSpan={columns.length} textAlign="center" color="gray.400">({viewAcceptingOffers ? 'No players have received offers' : 'No players found'})</Td>
                  </Tr>
                ) : (
                  tablePlayers.map(entry => (
                    <Tr
                      key={entry.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(entry)}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(44, 130, 201, 0.15)')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                    >
                      {columns.map((col, index) => (
                        <Td
                          key={col.key}
                          data-col-key={col.key}
                          {...(index === 0 ? { 'data-sticky-surface': "1", position: "sticky", left: "0", zIndex: 10, bg: stickyBg } : {})}
                          {...(col.tdProps || {})}
                        >
                          {col.render(entry, isWaiverWireView)}
                        </Td>
                      ))}
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Box>
      {/* PlayerCard Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none" border="none">
          <ModalBody p={0}>
            {selectedPlayer && (
              <PlayerCard
                player={selectedPlayer}
                team={freeAgentTeam}
                subListLabel={tabName}
                onClose={onClose}
                isFreeAgentView={!isWaiverWireView}
                isWaiverWireView={isWaiverWireView}
                isMyTeam={myTeam && selectedPlayer.team_id && Number(myTeam) === Number(selectedPlayer.team_id)}
                userAction={handleUserAction}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Sign Free Agent Modal */}
      {signModalPlayer && (
        <SignFreeAgentModal
          isOpen={isSignModalOpen}
          onClose={() => {
            setIsSignModalOpen(false);
            setSignModalPlayer(null);
            setSignModalDealId(null);
            setSignModalInitialSalaries(undefined);
          }}
          player={signModalPlayer}
          level={signModalLevel}
          refetch={refetch}
          existingDealId={signModalDealId}
          initialYearlySalaries={signModalInitialSalaries}
        />
      )}

      {/* Stripe Pre-Approval Modal for Salary Cap Exceedance */}
      <StripePaymentForm
        isOpen={isPreApprovalOpen}
        onClose={handlePreApprovalClose}
        amount={preApprovalAmount}
        payload={{
          type: 'waiver_claim_salary_cap_exceedance',
          playerId: pendingClaimPlayer?.id || undefined,
          teamId: myTeam || undefined,
          leagueId: myLeague?.id || undefined,
        }}
        onSuccess={handlePreApprovalSuccess}
      />
    </Box>
  );
};

export default AvailablePlayersContent; 